/**
 * Column definitions and display utilities for TargetSelector.
 *
 * Exports:
 * - TargetItem: interface for target data
 * - resolveTargetDisplay: display logic moved here from TargetSelector
 * - getTargetColumns: returns ColumnDef[] per target type
 */

import { type ColumnDef } from '@tanstack/react-table';
import React from 'react';

import { parseKeys } from '@/sections/SearchByType';

export interface TargetItem {
  key: string;
  group?: string;
  identifier?: string;
  source?: string;
  name?: string;
  value?: string;
  domain?: string;
  dns?: string;
  url?: string;
  service?: string;
  port?: number;
}

/**
 * Result of resolving target display data.
 * - text: Plain text representation (always present)
 * - jsx: Optional JSX representation (for complex rendering)
 */
interface DisplayResult {
  text: string;
  jsx?: React.ReactNode;
}

/**
 * Resolve display data for a target item.
 * Single source of truth for all target display logic.
 */
export function resolveTargetDisplay(
  item: TargetItem,
  type: string
): DisplayResult {
  switch (type) {
    case 'asset':
      return { text: item.group || item.identifier || item.key };

    case 'attribute':
      if (item.source) {
        const parsedAsset = parseKeys.assetKey(item.source);
        const text = `${parsedAsset.identifier} - ${item.name}:${item.value}`;
        const jsx = (
          <span>
            {parsedAsset.identifier} - {item.name}:{item.value}
          </span>
        );
        return { text, jsx };
      }
      if (item.name && item.value) {
        return { text: `${item.name}:${item.value}` };
      }
      return { text: item.name || item.key };

    case 'addomain':
      return {
        text: (
          item.domain ||
          item.group ||
          item.name ||
          item.key
        ).toUpperCase(),
      };

    case 'domain':
      return { text: item.dns || item.name || item.key };

    case 'seed':
      return { text: item.identifier || item.key };

    case 'preseed':
      return { text: item.name || item.dns || item.key };

    case 'webpage':
      return { text: item.url || item.name || item.key };

    case 'repository':
      return { text: item.group || item.name || item.key };

    case 'port': {
      const portData = parseKeys.portKey(item.key);
      const assetData = parseKeys.assetKey(portData.assetKey);
      const service = item.service ? ` (${item.service})` : '';
      const text = `${assetData.identifier} - ${portData.protocol.toUpperCase()}/${portData.port}${service}`;
      const jsx = (
        <span>
          {assetData.identifier} - {portData.protocol.toUpperCase()}/
          {portData.port}
          {service}
        </span>
      );
      return { text, jsx };
    }

    default:
      return { text: item.name || item.dns || item.key };
  }
}

/**
 * Returns column definitions for a given target type.
 *
 * For 'port': two columns — Asset and Port+Protocol.
 * For all other types: one column — Target display value.
 */
export function getTargetColumns(
  targetType: string
): ColumnDef<TargetItem, unknown>[] {
  if (targetType === 'port') {
    return [
      {
        id: 'asset',
        header: 'Asset',
        accessorFn: (row: TargetItem) => {
          try {
            const portData = parseKeys.portKey(row.key);
            const assetData = parseKeys.assetKey(portData.assetKey);
            return assetData.identifier;
          } catch {
            return row.key;
          }
        },
        cell: ({ getValue }) => (
          <span className="text-chariot-text-primary text-sm">
            {getValue() as string}
          </span>
        ),
        meta: { flexible: true },
      },
      {
        id: 'port',
        header: 'Port',
        size: 160,
        accessorFn: (row: TargetItem) => {
          try {
            const portData = parseKeys.portKey(row.key);
            const service = row.service ? ` (${row.service})` : '';
            return `${portData.protocol.toUpperCase()}/${portData.port}${service}`;
          } catch {
            return String(row.port ?? '');
          }
        },
        cell: ({ getValue }) => (
          <span className="text-chariot-text-primary text-sm">
            {getValue() as string}
          </span>
        ),
      },
    ];
  }

  return [
    {
      id: 'target',
      header: 'Target',
      accessorFn: (row: TargetItem) =>
        resolveTargetDisplay(row, targetType).text,
      cell: ({ row }) => {
        const result = resolveTargetDisplay(row.original, targetType);
        return (
          <span className="text-chariot-text-primary text-sm">
            {result.jsx ?? result.text}
          </span>
        );
      },
      meta: { flexible: true },
    },
  ];
}
